import { useCallback, useMemo, useState } from 'react';

import * as monthUtils from '@actual-app/core/shared/months';
import { q } from '@actual-app/core/shared/query';

import { calcularCuadre } from '#components/mobile/inicio/cuadre';
import type {
  DatosCuadre,
  ResultadoCuadre,
} from '#components/mobile/inicio/cuadre';
import { useSheetValue } from '#hooks/useSheetValue';
import { envelopeBudget } from '#spreadsheet/bindings';

// Sumas en vivo (consultas de la hoja, como los saldos de cuenta). `inline`:
// los divididos cuentan por sus partes. Las cuentas cerradas también cuentan.
// Solo hasta hoy (fecha local), como el saldo que ve el usuario.
function sumas(mes: string, hoy: string) {
  const base = q('transactions')
    .filter({ 'account.offbudget': false })
    .options({ splits: 'inline' });
  return {
    saldo: {
      name: 'cuadre-saldo',
      query: base
        .filter({ date: { $lte: hoy } })
        .calculate({ $sum: '$amount' }),
    },
    sinCategoria: {
      name: 'cuadre-sin-categoria',
      query: base
        .filter({ category: null, date: { $lte: hoy } })
        .calculate({ $sum: '$amount' }),
    },
    apuntadosFuturos: {
      name: 'cuadre-apuntados-futuros',
      query: base
        .filter({
          category: { $ne: null },
          $and: [
            { date: { $gt: hoy } },
            { date: { $transform: '$month', $lte: mes } },
          ],
        })
        .calculate({ $sum: '$amount' }),
    },
    mesesSiguientes: {
      name: 'cuadre-meses-siguientes',
      query: base
        .filter({
          category: { $ne: null },
          $and: [
            { date: { $lte: hoy } },
            { date: { $transform: '$month', $gt: mes } },
          ],
        })
        .calculate({ $sum: '$amount' }),
    },
  } as const;
}

type CampoSuma =
  | 'cuadre-saldo'
  | 'cuadre-sin-categoria'
  | 'cuadre-apuntados-futuros'
  | 'cuadre-meses-siguientes';

/**
 * Una suma de la hoja. SQL da `null` al sumar cero filas, así que «ha
 * llegado» se sabe por el aviso de cambio, no por el valor.
 */
function useSuma(binding: {
  name: CampoSuma;
  query: ReturnType<typeof q>;
}): number | null {
  const [llegado, setLlegado] = useState(false);
  const alCambiar = useCallback(() => setLlegado(true), []);
  const valor = useSheetValue<'envelope-budget', CampoSuma>(binding, alCambiar);
  if (!llegado) {
    return null;
  }
  return typeof valor === 'number' ? valor : 0;
}

/**
 * Datos del cuadre del mes (ver `cuadre.ts`). Necesita estar dentro del
 * `SheetNameProvider` del mes. `null` mientras falte algún dato.
 */
export function useCuadre(
  mes: string,
): (ResultadoCuadre & { datos: DatosCuadre }) | null {
  const hoy = monthUtils.currentDay();
  const s = useMemo(() => sumas(mes, hoy), [mes, hoy]);
  const vSaldo = useSuma(s.saldo);
  const vSin = useSuma(s.sinCategoria);
  const vApuntados = useSuma(s.apuntadosFuturos);
  const vSiguientes = useSuma(s.mesesSiguientes);
  const listo = useSheetValue<'envelope-budget', 'to-budget'>(
    envelopeBudget.toBudget,
  );
  const futuroAsignado = useSheetValue<'envelope-budget', 'future-budgeted'>(
    'future-budgeted',
  );
  const guardado = useSheetValue<'envelope-budget', 'buffered-selected'>(
    envelopeBudget.forNextMonth,
  );
  const disponible = useSheetValue<'envelope-budget', 'total-leftover'>(
    envelopeBudget.totalBalance,
  );

  return useMemo(() => {
    const valores = [
      vSaldo,
      vSin,
      vApuntados,
      vSiguientes,
      listo,
      futuroAsignado,
      guardado,
      disponible,
    ];
    if (valores.some(v => typeof v !== 'number')) {
      return null;
    }
    const datos: DatosCuadre = {
      saldoCuentas: vSaldo as number,
      listoParaAsignar: listo as number,
      asignadoFuturo: -(futuroAsignado as number),
      guardado: guardado as number,
      disponible: disponible as number,
      apuntadosFuturos: vApuntados as number,
      mesesSiguientes: vSiguientes as number,
      sinCategoria: vSin as number,
    };
    return { ...calcularCuadre(datos), datos };
  }, [
    vSaldo,
    vSin,
    vApuntados,
    vSiguientes,
    listo,
    futuroAsignado,
    guardado,
    disponible,
  ]);
}
